// shapes.tsx — DESIGN INSPIRATION: how shapes.co shows its own product.
//
// Erez, 27 Aug 2026: "I want us to take design inspiration from this website.
// Can you extract all the images that show the screens of the app? Go through
// the whole website and capture all the images that show how the app looks…
// then let's talk about this design pattern and learn what it is."
//
// ── WHAT WAS ACTUALLY DONE ──────────────────────────────────────────────────
// Crawled all 20 pages of shapes.co (a Webflow build; every asset sits on
// cdn.prod.website-files.com), pulled 310 unique assets, downloaded the 247
// rasters, measured every one, and then LOOKED at the 135 that are bigger than
// 200×200 — on labelled contact sheets, twelve at a time. 92 of them show the
// product. The other 43 are headshots, customer logos, lifestyle photography
// with no UI in it, decorative gradients and the OG preview image.
//
// ⛔ THE SELECTION WAS MADE BY EYE, DELIBERATELY, AND THAT IS THE POINT.
// A pixel-size threshold would have been quicker and would have been the wrong
// instrument: the question is not "which of these rasters is large?" but "which
// of these shows the app?", and no size statistic can reach it. The eight
// nav-hover images on the homepage are 1064×736 photographs of people in an
// office — big, and not the product. Meanwhile the smallest real finding here
// is 660×374. Sorting by area would have kept the first and dropped the second.
//
// ── THE THREE GROUPS ARE THE FINDING, NOT A FILING CONVENIENCE ──────────────
// Sorting the 92 by hand, they fall into exactly three treatments, and the site
// uses them in a strict rhythm — one WHOLE SCREEN as the hero of a page, then
// FRAGMENTS for everything below it. There is no fourth treatment anywhere on
// the site, and there is no page that opens on a fragment.
//
//   full   (24)  The app in its own window chrome — sidebar, titlebar, traffic
//                lights — on a soft colour wash. Never on white, never bleeding
//                to the edge of the section.
//   frag   (31)  ONE control, card or answer cropped out of the product and set
//                on flat cream. Not a cropped screenshot of a screen: a single
//                object, alone, with the rest of the app absent.
//   photo  (37)  The same cropped card floated over full-bleed editorial
//                photography. This is the signature move and it is where the
//                whole personality of the site lives.
//
// ── ASSETS ──────────────────────────────────────────────────────────────────
// 92 files on R2 at board/outform/shapes/, re-encoded to webp at 1400px wide
// (6.2MB total, 69KB average). NOT avif, though the sources are: encoding 92
// avifs pegged this 4-core box at load 7 with five other agents on it, and a
// background job has to budget for the interactive services beside it. The
// manifest is inlined below rather than fetched — it is 11KB, so a fetch would
// buy a CORS dependency and a loading state for nothing. Nothing of the
// crawl is committed to this repo.

const R2 = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/board/outform/shapes";
const V = "1"; // STATIC cache tag. Never `?t=${Date.now()}` — that re-downloads
               // every megabyte on every board open (board law, 2026-07-20).
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

type Group = "full" | "frag" | "photo";
type Shot = { f: string; g: Group; p: string; w: number; h: number; l: string };

const SHOTS: Shot[] = [
  { f: "full-021.webp", g: "full", p: "home", w: 1400, h: 792, l: "Homescreen" },
  { f: "full-034.webp", g: "full", p: "home", w: 1046, h: 592, l: "Homescreen" },
  { f: "full-002.webp", g: "full", p: "ai-page", w: 1400, h: 792, l: "Vibe studio" },
  { f: "full-052.webp", g: "full", p: "mcp", w: 1400, h: 1096, l: "Generate a QBR deck" },
  { f: "full-055.webp", g: "full", p: "new-releases", w: 1200, h: 520, l: "Edit wallpaper" },
  { f: "full-059.webp", g: "full", p: "partnerships", w: 987, h: 653, l: "Org chart" },
  { f: "full-060.webp", g: "full", p: "product/attendance", w: 1400, h: 742, l: "Time management" },
  { f: "full-061.webp", g: "full", p: "product/attendance", w: 1400, h: 719, l: "Time management" },
  { f: "full-067.webp", g: "full", p: "product/compensation", w: 1400, h: 719, l: "Compensation cycle" },
  { f: "full-072.webp", g: "full", p: "product/docs-and-sign", w: 1400, h: 742, l: "Employee profile" },
  { f: "full-073.webp", g: "full", p: "product/docs-and-sign", w: 1400, h: 719, l: "Knowledge base" },
  { f: "full-079.webp", g: "full", p: "product/people-analytics", w: 1400, h: 742, l: "Time management" },
  { f: "full-080.webp", g: "full", p: "product/people-analytics", w: 1400, h: 719, l: "Insights that push businesses forward" },
  { f: "full-085.webp", g: "full", p: "product/people-directory", w: 1400, h: 698, l: "People — overview" },
  { f: "full-086.webp", g: "full", p: "product/people-directory", w: 1400, h: 719, l: "People — list" },
  { f: "full-091.webp", g: "full", p: "product/performance-and-growth", w: 1400, h: 742, l: "Employee engagement" },
  { f: "full-092.webp", g: "full", p: "product/performance-and-growth", w: 1400, h: 719, l: "Quarterly KPI assessment" },
  { f: "full-098.webp", g: "full", p: "product/surveys", w: 1400, h: 742, l: "Engagement by question" },
  { f: "full-099.webp", g: "full", p: "product/surveys", w: 1400, h: 719, l: "Survey AI summary" },
  { f: "full-105.webp", g: "full", p: "product/time-off", w: 1400, h: 742, l: "Time-off reports" },
  { f: "full-106.webp", g: "full", p: "product/time-off", w: 1400, h: 719, l: "Time management" },
  { f: "full-111.webp", g: "full", p: "product/workflows-shapes", w: 1400, h: 742, l: "Workflow runs" },
  { f: "full-112.webp", g: "full", p: "product/workflows-shapes", w: 1400, h: 719, l: "Pre-onboarding checklist" },
  { f: "full-117.webp", g: "full", p: "security-and-compliance", w: 1380, h: 870, l: "Scoped permissions" },
  { f: "frag-022.webp", g: "frag", p: "home", w: 1078, h: 982, l: "Survey responses" },
  { f: "frag-023.webp", g: "frag", p: "home", w: 1314, h: 686, l: "People list, with status" },
  { f: "frag-031.webp", g: "frag", p: "home", w: 960, h: 669, l: "Approve a holiday" },
  { f: "frag-032.webp", g: "frag", p: "home", w: 687, h: 916, l: "Workflow templates" },
  { f: "frag-033.webp", g: "frag", p: "home", w: 1005, h: 621, l: "Docs & esign" },
  { f: "frag-035.webp", g: "frag", p: "home", w: 897, h: 686, l: "Clocked in — 8h 27m" },
  { f: "frag-036.webp", g: "frag", p: "home", w: 822, h: 648, l: "Headcount trend" },
  { f: "frag-046.webp", g: "frag", p: "home", w: 532, h: 225, l: "Help desk agent" },
  { f: "frag-047.webp", g: "frag", p: "home", w: 532, h: 220, l: "Time-off agent" },
  { f: "frag-048.webp", g: "frag", p: "home", w: 532, h: 220, l: "Compensation agent" },
  { f: "frag-053.webp", g: "frag", p: "mcp", w: 1316, h: 870, l: "Ask, and it routes to your tools" },
  { f: "frag-054.webp", g: "frag", p: "mcp", w: 1316, h: 870, l: "Ask anything" },
  { f: "frag-062.webp", g: "frag", p: "product/attendance", w: 1380, h: 870, l: "Custom payroll cycles" },
  { f: "frag-063.webp", g: "frag", p: "product/attendance", w: 1380, h: 870, l: "Robust time tracking rules" },
  { f: "frag-069.webp", g: "frag", p: "product/compensation", w: 1380, h: 870, l: "FX rates" },
  { f: "frag-074.webp", g: "frag", p: "product/docs-and-sign", w: 1380, h: 870, l: "Get instant answers with AI" },
  { f: "frag-075.webp", g: "frag", p: "product/docs-and-sign", w: 1380, h: 870, l: "Compensation history" },
  { f: "frag-081.webp", g: "frag", p: "product/people-analytics", w: 1380, h: 870, l: "Ask the AI chat" },
  { f: "frag-082.webp", g: "frag", p: "product/people-analytics", w: 1380, h: 870, l: "Your reviews and surveys analyzed" },
  { f: "frag-083.webp", g: "frag", p: "product/people-analytics", w: 1380, h: 870, l: "AI-powered insights" },
  { f: "frag-087.webp", g: "frag", p: "product/people-directory", w: 1380, h: 870, l: "Table builder" },
  { f: "frag-088.webp", g: "frag", p: "product/people-directory", w: 1380, h: 870, l: "Ask the AI chat" },
  { f: "frag-093.webp", g: "frag", p: "product/performance-and-growth", w: 1380, h: 870, l: "Set & track goals" },
  { f: "frag-094.webp", g: "frag", p: "product/performance-and-growth", w: 1380, h: 870, l: "Cycle engagement analysis" },
  { f: "frag-100.webp", g: "frag", p: "product/surveys", w: 1380, h: 870, l: "Ask anonymously" },
  { f: "frag-101.webp", g: "frag", p: "product/surveys", w: 1380, h: 870, l: "AI insights" },
  { f: "frag-107.webp", g: "frag", p: "product/time-off", w: 1380, h: 870, l: "Build policies in minutes" },
  { f: "frag-108.webp", g: "frag", p: "product/time-off", w: 1380, h: 870, l: "See who s out at a glance" },
  { f: "frag-113.webp", g: "frag", p: "product/workflows-shapes", w: 1380, h: 870, l: "ESign automated" },
  { f: "frag-114.webp", g: "frag", p: "product/workflows-shapes", w: 1380, h: 870, l: "Create workflows with AI" },
  { f: "frag-118.webp", g: "frag", p: "security-and-compliance", w: 1316, h: 870, l: "Ask, and it routes to your tools" },
  { f: "photo-001.webp", g: "photo", p: "ai-page", w: 1400, h: 883, l: "" },
  { f: "photo-004.webp", g: "photo", p: "ai-page", w: 1035, h: 653, l: "Ask anonymously" },
  { f: "photo-005.webp", g: "photo", p: "ai-page", w: 1035, h: 653, l: "Time off heat map" },
  { f: "photo-037.webp", g: "photo", p: "home", w: 660, h: 374, l: "Trend line" },
  { f: "photo-038.webp", g: "photo", p: "home", w: 660, h: 374, l: "Absence heat map" },
  { f: "photo-039.webp", g: "photo", p: "home", w: 660, h: 374, l: "Create a team" },
  { f: "photo-040.webp", g: "photo", p: "home", w: 660, h: 374, l: "High performers" },
  { f: "photo-042.webp", g: "photo", p: "home", w: 660, h: 374, l: "Avg unused days" },
  { f: "photo-043.webp", g: "photo", p: "home", w: 660, h: 374, l: "Options value" },
  { f: "photo-044.webp", g: "photo", p: "home", w: 660, h: 374, l: "Task status" },
  { f: "photo-064.webp", g: "photo", p: "product/attendance", w: 1290, h: 870, l: "Clock in & out" },
  { f: "photo-065.webp", g: "photo", p: "product/attendance", w: 1290, h: 860, l: "Smart notifications" },
  { f: "photo-076.webp", g: "photo", p: "product/docs-and-sign", w: 1290, h: 860, l: "ESign simplified" },
  { f: "photo-077.webp", g: "photo", p: "product/docs-and-sign", w: 1290, h: 860, l: "Your people knowledge base" },
  { f: "photo-084.webp", g: "photo", p: "product/people-analytics", w: 1290, h: 860, l: "Unlimited reports" },
  { f: "photo-089.webp", g: "photo", p: "product/people-directory", w: 1290, h: 860, l: "Calendar view fully in sync" },
  { f: "photo-090.webp", g: "photo", p: "product/people-directory", w: 1290, h: 860, l: "Get the lay of the land" },
  { f: "photo-095.webp", g: "photo", p: "product/performance-and-growth", w: 1290, h: 860, l: "Build tailored cycles in seconds" },
  { f: "photo-096.webp", g: "photo", p: "product/performance-and-growth", w: 1290, h: 860, l: "Employee quick summary" },
  { f: "photo-102.webp", g: "photo", p: "product/surveys", w: 1290, h: 860, l: "Launch surveys in minutes" },
  { f: "photo-103.webp", g: "photo", p: "product/surveys", w: 1290, h: 860, l: "Measure engagement" },
  { f: "photo-109.webp", g: "photo", p: "product/time-off", w: 1290, h: 860, l: "Configure everything your way" },
  { f: "photo-110.webp", g: "photo", p: "product/time-off", w: 1290, h: 860, l: "Easy vacation requests" },
  { f: "photo-115.webp", g: "photo", p: "product/workflows-shapes", w: 1290, h: 860, l: "Template center" },
  { f: "photo-116.webp", g: "photo", p: "product/workflows-shapes", w: 1290, h: 860, l: "Automate any process" },
  { f: "photo-119.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Performance review trends" },
  { f: "photo-120.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Data gaps" },
  { f: "photo-121.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Skills library" },
  { f: "photo-122.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "People ops command center" },
  { f: "photo-123.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Data directory" },
  { f: "photo-124.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Salary band generator" },
  { f: "photo-125.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Time off heatmap" },
  { f: "photo-126.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Manager Directory" },
  { f: "photo-127.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Emergency contact org chart" },
  { f: "photo-128.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Bradford score" },
  { f: "photo-129.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Access & Permissions" },
  { f: "photo-130.webp", g: "photo", p: "vibe-views", w: 1320, h: 940, l: "Talent Intelligence" },
];

const GROUPS: { key: Group; n: string; rule: string; note: string; tile: number }[] = [
  {
    key: "full", n: "The whole screen", rule: "one per page, always the hero",
    note:
      "App in its own window chrome — sidebar, titlebar, traffic lights — floated on a soft colour wash. " +
      "It is never on white and never bleeds to the section edge, so it reads as a THING sitting on a surface " +
      "rather than as a picture pasted into the page.",
    tile: 620,
  },
  {
    key: "frag", n: "A fragment on flat colour", rule: "one idea, one object",
    note:
      "ONE control, card or answer lifted out of the product and set on flat cream. Not a cropped screenshot — " +
      "a single object with the rest of the app absent. Nothing here needs a caption to be understood, which is " +
      "the test it was designed to pass.",
    tile: 430,
  },
  {
    key: "photo", n: "A fragment on a photograph", rule: "the signature move",
    note:
      "The same cropped card floated over full-bleed editorial photography — a pool, an elevator, a baby, a beach. " +
      "The photograph carries the human situation; the card carries the software's answer to it. Neither could do " +
      "the job alone and the pairing is the entire personality of the site.",
    tile: 430,
  },
];

function Tile({ s, w }: { s: Shot; w: number }) {
  // data-zoom: double-click frames this tile, so a 1400px screenshot rendered
  // at 430 becomes readable without leaving the canvas. BoardCanvas's own
  // gesture — a marked child wins over the section via closest().
  return (
    <div
      data-zoom
      style={{
        width: w, background: "#fff", border: "1px solid #E7E5E4", borderRadius: 10,
        overflow: "hidden", fontFamily: FONT, boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      <img
        src={`${R2}/${s.f}?v=${V}`}
        alt={s.l || s.p}
        width={s.w}
        height={s.h}
        decoding="async"
        /* ⛔ never loading="lazy" in the zoom canvas — an unloaded image is 0px
           tall and the whole section collapses (board law). */
        style={{ width: "100%", height: "auto", display: "block", background: "#FAFAF9" }}
      />
      <div style={{ padding: "7px 10px 9px", borderTop: "1px solid #F5F5F4" }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1C1917", lineHeight: 1.3 }}>
          {s.l || "—"}
        </div>
        <div style={{ fontSize: 10, color: "#A8A29E", fontFamily: MONO, marginTop: 2 }}>
          shapes.co/{s.p === "home" ? "" : s.p}
        </div>
      </div>
    </div>
  );
}

export function ShapesInspiration({ width = 5980 }: { width?: number }) {
  return (
    <div style={{ width, fontFamily: FONT }}>
      <div style={{
        display: "flex", gap: 26, alignItems: "baseline", marginBottom: 26,
        paddingBottom: 16, borderBottom: "2px solid #D2D2D2",
      }}>
        <span style={{ fontSize: 30, fontWeight: 800, color: "#1C1917", letterSpacing: "-0.02em" }}>
          92 product images · 20 pages · 3 treatments
        </span>
        <span style={{ fontSize: 16, color: "#57534E", lineHeight: 1.4 }}>
          Every image on shapes.co that shows the app, pulled from all 20 pages and grouped by
          <b> how it is presented</b> rather than by which page it came from — because the three
          treatments are the pattern. Double-click any tile to read it.
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
        {GROUPS.map((g) => {
          const shots = SHOTS.filter((s) => s.g === g.key);
          return (
            <div key={g.key}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 4 }}>
                <span style={{
                  fontSize: 19, fontWeight: 800, color: "#fff", background: "#1C1917",
                  borderRadius: 6, padding: "3px 12px", letterSpacing: "-0.01em",
                }}>{g.n}</span>
                <span style={{ fontSize: 16, fontWeight: 600, color: "#78716C" }}>{g.rule}</span>
                <span style={{ fontSize: 13, color: "#A8A29E", fontFamily: MONO }}>{shots.length} images</span>
              </div>
              <div style={{ fontSize: 15, color: "#44403C", lineHeight: 1.5, maxWidth: 1500, marginBottom: 14 }}>
                {g.note}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                {shots.map((s) => <Tile key={s.f} s={s} w={g.tile} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
